import { array, bool, nullable, num, object, str, unknownValue, type FieldParser, type ObjectShape } from 'validation';

// Shared wire shapes for the node-instance read endpoints, all served by
// nap-backend `getNodeInstances.js`:
//   - GET /node_instances               -> flat paginated list (SELECT ~490-645)
//   - GET /node_instances/:id           -> recursive subtree (root SELECT ~141-388,
//                                           children SELECT ~1172-1359)
//   - GET /node_instances_flat/:id       -> flattened flow (root SELECT ~1416-1654,
//                                           children SELECT ~1782-1905, edges ~2022-2049)
//
// DELIBERATE snake_case: unlike the camelCase-at-UI convention (and unlike
// `getNodeInstanceTree`'s `toSummary` projection), these loaders keep the wire's
// snake_case keys. The list table renders columns generically off the server
// `config` array — `useTableController.generateColumns` sets
// `accessorKey: header_key` straight onto these rows — and `useAdvanceTable`
// indexes `child_nodes`/`definition_id` on raw rows. Renaming would break both.
//
// DELIBERATELY mutable (no `readonly`): rows are stored in Redux (immer) state
// and mutated by plain-typed reducer helpers outside draft context
// (`updateNodeEverywhere`, `removeFromChildren`), which deep-`readonly` fields
// would reject.
//
// `object()` DROPS unknown wire fields, so any field a consumer reads must be
// declared here — the backend SELECTs named above are the source of truth.
// JSON aggregates whose inner shape no consumer narrows against are typed
// `unknown` via `unknownValue()`.

/**
 * The node-instance columns common to all three endpoints' primary row shapes
 * (flat-list row, subtree root, flat root). Endpoint-specific extras are added
 * by each loader's own schema.
 *
 * Aliases that differ per endpoint are intentionally NOT here: the flat list
 * sends `file_event_name` while the subtree/flat roots send `event_name`, and
 * `file_event_zone_name`/`queue_zone_name`/`has_child_nodes` exist on only some
 * branches.
 */
export interface NodeInstanceRowCore {
  node_instance_id: number;
  parent_node_instance_id: number | null;
  node_definition_id: number;
  definition_version: number | null;
  name: string;
  description: string | null;
  type: string;
  sub_type: string | null;
  status: string;
  sub_status: string | null;
  scheduled_start_datetime: string | null;
  scheduled_start_timezone: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  runtime_limit_min: number | null;
  runtime_limit_max: number | null;
  duration: number | null;
  created_at: string;
  // Raw node_instances.schedule JSONB: either a single legacy recurrence object
  // or `{ recurrences: [...], resubmit_on_error }`. The list stringifies it; the
  // backend also derives a `schedule_display` string.
  schedule: unknown;
  recurrence_definition_id: number | null;
  // Recurrence-chain fields (nap-backend #939). `recurrence_definition` is the
  // linked definition's "zone|name" path (null for ad-hoc-scheduled instances);
  // `schedule_display` is the human-readable render of `schedule` ('' when the
  // instance is definition-linked and carries no ad-hoc schedule).
  recurrence_definition: string | null;
  schedule_display: string;
  previous_node_instance_id: number | null;
  original_anchor_datetime: string | null;
  queue_name: string | null;
  worker_name: string | null;
  zone_name: string | null;
  // ARRAY_AGG(DISTINCT lock name) -> text[] or null.
  lock_name: string[] | null;
  // CASE -> "zone|window" or null.
  scheduling_window: string | null;
  has_skippable_wait_events: boolean;
  file_event_instance_id: number | null;
  process_event_name: string | null;
  connection_name: string | null;
  label: string | null;
  spawned_node_instance_id: number | null;
  originating_node_instance_id: number | null;
  originating_node_definition_name: string | null;
  originating_flow_instance_id: number | null;
  originating_flow_definition_name: string | null;
  scheduled_by: string | null;
}

/** Field parsers for {@link NodeInstanceRowCore}, spread into each loader's schema shape. */
export const nodeInstanceRowCoreFields: ObjectShape<NodeInstanceRowCore> = {
  node_instance_id: num(),
  parent_node_instance_id: nullable(num()),
  node_definition_id: num(),
  definition_version: nullable(num()),
  name: str(),
  description: nullable(str()),
  type: str(),
  sub_type: nullable(str()),
  status: str(),
  sub_status: nullable(str()),
  scheduled_start_datetime: nullable(str()),
  scheduled_start_timezone: nullable(str()),
  start_datetime: nullable(str()),
  end_datetime: nullable(str()),
  runtime_limit_min: nullable(num()),
  runtime_limit_max: nullable(num()),
  duration: nullable(num()),
  created_at: str(),
  schedule: unknownValue(),
  recurrence_definition_id: nullable(num()),
  recurrence_definition: nullable(str()),
  schedule_display: str(),
  previous_node_instance_id: nullable(num()),
  original_anchor_datetime: nullable(str()),
  queue_name: nullable(str()),
  worker_name: nullable(str()),
  zone_name: nullable(str()),
  lock_name: nullable(array(str())),
  scheduling_window: nullable(str()),
  has_skippable_wait_events: bool(),
  file_event_instance_id: nullable(num()),
  process_event_name: nullable(str()),
  connection_name: nullable(str()),
  label: nullable(str()),
  spawned_node_instance_id: nullable(num()),
  originating_node_instance_id: nullable(num()),
  originating_node_definition_name: nullable(str()),
  originating_flow_instance_id: nullable(num()),
  originating_flow_definition_name: nullable(str()),
  scheduled_by: nullable(str()),
};

/**
 * One edge of the flattened flow graph (`GET /node_instances_flat/:id`,
 * `fetchAllChildEdges` SELECT ~2022-2049). `source`/`target` are flow-node
 * string ids; the joined node-instance ids are nullable (LEFT JOIN).
 */
export interface NodeInstanceChildEdge {
  source: string | null;
  target: string | null;
  type: string | null;
  source_handle: string | null;
  target_handle: string | null;
  source_iteration: number | null;
  target_iteration: number | null;
  parent_instance_id: number | null;
  source_node_instance_id: number | null;
  target_node_instance_id: number | null;
}

export const nodeInstanceChildEdgeSchema: FieldParser<NodeInstanceChildEdge> = object<NodeInstanceChildEdge>({
  source: nullable(str()),
  target: nullable(str()),
  type: nullable(str()),
  source_handle: nullable(str()),
  target_handle: nullable(str()),
  source_iteration: nullable(num()),
  target_iteration: nullable(num()),
  parent_instance_id: nullable(num()),
  source_node_instance_id: nullable(num()),
  target_node_instance_id: nullable(num()),
});
